import { http } from '@/app/lib/http/client'
import type { AuthUser, PermissionMatrix } from '@/app/store/auth/auth.store'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LoginPayload {
    email: string
    password: string
    device_uuid: string
    device_name: string
    sucursal_id: number
}

export interface LoginUsersPayload {
    device_uuid: string
    sucursal_id: number
}

export interface PinLoginPayload {
    user_id: number
    pin: string
    device_uuid: string
    sucursal_id: number
}

export interface CredentialLoginPayload {
    user_id: number
    email: string
    password: string
    device_uuid: string
    sucursal_id: number
}

export interface AuthResponse {
    token: string
    token_type: string
    user: AuthUser
}

export interface SucursalContextResponse {
    sucursal: { id: number; name: string }
    device: { id: number; name: string }
}

// ─── Service ──────────────────────────────────────────────────────────────────

export const authService = {
    async csrf() {
        const apiBase = import.meta.env.VITE_API_URL.replace(/\/api\/v1\/?$/, '')
        await http.get(`${apiBase}/sanctum/csrf-cookie`)
    },

    /**
     * Login de primer acceso / admin. Genera un dispositivo de confianza.
     * Requiere email, password y datos del dispositivo.
     */
    async login(payload: LoginPayload): Promise<AuthResponse> {
        await this.csrf()
        const response = await http.post('/auth/login', payload)
        console.log(response)
        return response.data.data
    },

    /**
     * Obtiene la lista de usuarios disponibles en un dispositivo de confianza.
     * Se usa para mostrar los avatares en la pantalla de login por PIN.
     */
    async loginUsers(payload: LoginUsersPayload): Promise<AuthUser[]> {
        await this.csrf()
        const response = await http.post('/auth/login-users', payload)
        return response.data.data
    },

    /**
     * Obtiene la lista publica de sucursales activas para seleccionar al registrar un dispositivo.
     */
    async getSucursales(): Promise<{id: number, name: string}[]> {
        const response = await http.get('/auth/sucursales')
        return response.data.data
    },

    async getSucursalContext(): Promise<SucursalContextResponse> {
        const response = await http.get('/auth/sucursal-context')
        return response.data.data
    },

    async switchSucursalContext(payload: {
        sucursal_id: number
        expected_sucursal_id: number
    }): Promise<SucursalContextResponse> {
        const response = await http.put('/auth/sucursal-context', payload)
        return response.data.data
    },

    /**
     * Login por PIN para usuarios con use_pin_login = true en un dispositivo confiable.
     */
    async pinLogin(payload: PinLoginPayload): Promise<AuthResponse> {
        await this.csrf()
        const response = await http.post('/auth/pin-login', payload)
        return response.data.data
    },

    /**
     * Login por credenciales para usuarios con use_pin_login = false en un dispositivo confiable.
     */
    async credentialLogin(payload: CredentialLoginPayload): Promise<AuthResponse> {
        await this.csrf()
        const response = await http.post('/auth/credential-login', payload)
        return response.data.data
    },

    async me(): Promise<AuthUser> {
        const response = await http.get('/auth/me')
        return response.data.data.user
    },

    async permissions(): Promise<PermissionMatrix> {
        const response = await http.get('/auth/permissions')
        return response.data.data
    },

    async logout(): Promise<void> {
        await http.post('/auth/logout')
    },
}
